import { SaveIcon, TrashIcon, ArrowLeftIcon, PlusIcon } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router';

// --- DATA & CONSTANTS (Keep these outside the component for clarity) ---
const data = {
    "id": "1",
    "name": "Grand Canyon University",
    "logo": "https://res.cloudinary.com/dk900kd01/image/upload/v1760335020/image_1_1_lcfilm.png",
    "style": [
        {
            "primaryTextColor": "white"
        },
        {
            "secondaryTextColor": "gray"
        },
        {
            "secondaryColor": "black"
        },
        {
            "primaryColor": "#7436D2"
        }
    ],
    "portalTheme": {
        "headerText": "Welcome to UNITE US at Grand Canyon University",
        "fields": [
            {
                "name": "Email",
                "type": "email",
                "placeholder": "Enter your email",
                "options": [],
                "_id": {
                    "$oid": "68ecb7a2636bef536468208c"
                }
            },
            {
                "name": "Gender",
                "type": "select",
                "options": [
                    "Male",
                    "Female",
                    "Other"
                ],
                "_id": {
                    "$oid": "68ecb7a2636bef536468208d"
                }
            },
            {
                "name": "Today I would say…",
                "type": "select",
                "options": [
                    "Saved",
                    "Rededicated",
                    "Want to go deeper",
                    "Already following",
                    "Not ready yet"
                ],
                "_id": {
                    "$oid": "68ecb7a2636bef536468208e"
                }
            },
            {
                "name": "Would you like to learn about campus ministries near you?",
                "type": "select",
                "options": [
                    "Yes",
                    "No",
                    "Already connected"
                ],
                "_id": {
                    "$oid": "68ecb7a2636bef536468208f"
                }
            }
        ],
        "ministrySelection": {
            "yes": [
                "Ministry 1",
                "Ministry 2",
                "Ministry 3",
                "Other"
            ]
        }
    },
    "completionPage": {
        "dynamicButtons": [
            {
                "label": "Churches near Grand Canyon University",
                "link": "https://www.klove.com/",
                "_id": {
                    "$oid": "68ecb7a2636bef5364682090"
                }
            },
            {
                "label": "Run with Christ",
                "link": "https://www.runwithchrist.org/gcu",
                "_id": {
                    "$oid": "68ecb7a2636bef5364682091"
                }
            },
            {
                "label": "Need Help?",
                "link": "https://www.gcu.edu/student-mentorship-request",
                "_id": {
                    "$oid": "68ecb7a2636bef5364682092"
                }
            },
            {
                "label": "Get Involved with UNITE",
                "link": "https://www.uniteus.org/volunteer-sign-up",
                "_id": {
                    "$oid": "68ecb7a2636bef5364682093"
                }
            },
            {
                "label": "I Got Saved / Rededicated",
                "link": "https://www.uniteus.org/follow-up-form",
                "_id": {
                    "$oid": "68ecb7a2636bef5364682094"
                }
            },
            {
                "label": "Interested in Baptism",
                "link": "https://www.uniteus.org/baptism-sign-up",
                "_id": {
                    "$oid": "68ecb7a2636bef5364682095"
                }
            }
        ]
    },
}
// Define the static keys and their initial values
const STATIC_COLORS = [
    {
        "primaryTextColor": "#FFFFFF"
    },
    {
        "secondaryTextColor": "#FFFFFF"
    },
    {
        "secondaryColor": "#FFFFFF"
    },
    {
        "primaryColor": "#FFFFFF"
    }
]
const dynamicButtons = [
    {
        "label": "Churches near Grand Canyon University",
        "link": "https://www.klove.com/",
    },
    {
        "label": "Run with Christ",
        "link": "https://www.runwithchrist.org/gcu",

    },
    {
        "label": "Need Help?",
        "link": "https://www.gcu.edu/student-mentorship-request",
    },
    {
        "label": "Get Involved with UNITE",
        "link": "https://www.uniteus.org/volunteer-sign-up",
    },
    {
        "label": "I Got Saved / Rededicated",
        "link": "https://www.uniteus.org/follow-up-form",
    },
    {
        "label": "Interested in Baptism",
        "link": "https://www.uniteus.org/baptism-sign-up",
    }
]

// --- CONSTANTS FOR STYLING ---
const ACTION_COLOR = '#FF9900'; // Bright Orange for main actions
const HOVER_COLOR = '#cc7a00'; // Darker orange for hover

const INPUT_CLASSES = "w-full p-3 rounded-xl bg-[var(--color-surface-input)] border border-[var(--border-color)] text-[var(--primary-text-color)] placeholder-gray-500 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors ";
const SECTION_TITLE_CLASSES = "text-2xl font-bold text-[var(--primary-text-color)] mb-6 border-b border-gray-700/50 pb-3";
const CARD_CLASSES = "bg-[var(--color-surface-background)] rounded-2xl border border-gray-800 p-8 shadow-2xl"; // Increased padding and shadow

// Helper function to ensure the style array contains the four static colors
const ensureStaticColors = (initialStyle) => {
    // Note: The original implementation in the prompt seems to map/check by the key of the *first* entry in the object.
    // We'll stick to a simple mapping for now since the keys are fixed.
    const styleMap = new Map((initialStyle || []).map(item => [Object.keys(item)[0], item]));

    return STATIC_COLORS.map(staticItem => {
        const key = Object.keys(staticItem)[0];
        // If the key exists in the initial style, use its value, otherwise use the default value.
        const existingItem = styleMap.get(key);
        return { [key]: existingItem ? existingItem[key] : staticItem[key] };
    });
};

// --- SUB-COMPONENTS ---

// Reusable IconButton Component
const IconButton = ({ icon: Icon, label, onClick, className = '', disabled = false, type = 'button' }) => (
    <button
        onClick={onClick}
        className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        aria-label={label}
        disabled={disabled}
        type={type}
    >
        <Icon className="w-5 h-5" />
        <span className='hidden sm:inline'>{label}</span>
    </button>
);

// New Component for Editing Select Options
const OptionListEditor = ({ options, onUpdateOptions, navigate }) => {
    const handleOptionChange = (index, value) => {
        const newOptions = [...options];
        newOptions[index] = value;
        onUpdateOptions(newOptions);
    };

    const addOption = () => {
        onUpdateOptions([...options, `New Option ${options.length + 1}`]);
    };

    const removeOption = (index) => {
        onUpdateOptions(options.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-3 p-4 bg-[var(--color-surface-card)] rounded-lg border border-gray-800">
            <p className="text-[var(--primary-text-color)] text-sm font-medium">Options List:</p>
            {options.map((option, index) => (
                <div key={index} className="flex items-center space-x-2">
                    <input
                        type="text"
                        value={option}
                        onChange={(e) => handleOptionChange(index, e.target.value)}
                        className={`flex-grow p-2 rounded-lg bg-[var(--color-surface-input)] border border-gray-700 text-[var(--primary-text-color)] placeholder-gray-500 focus:ring-1 focus:ring-orange-500 text-sm`}
                        placeholder={`Option ${index + 1}`}
                    />
                    <IconButton
                        icon={TrashIcon}
                        label="Remove"
                        onClick={() => removeOption(index)}
                        className="text-red-400 hover:bg-red-900/50 p-1 h-full !px-2"
                        title="Remove Option"
                    />
                </div>
            ))}
            <IconButton
                icon={PlusIcon}
                label="Add Option"
                onClick={addOption}
                className={`bg-[var(--color-primary)] cursor-pointer hover:bg-[var(--color-primary-dark)] text-[var(--primary-text-color)] w-full justify-center mt-3`}
            />
        </div>
    );
};

// --- MAIN COMPONENT ---

const ThemePortalConfig = ({ initialConfig = data, onSave, onCancel, isSaving }) => {
    // Determine if we are creating or updating based on whether _id exists
    const isUpdate = initialConfig?._id;
    const title = isUpdate ? `Update Configuration: ${initialConfig.name}` : "Create New School Configuration";

    // Define BLANK_CONFIG here to ensure correct field initialization structure
    const BLANK_CONFIG = {
        name: '', logo: '', id: '', style: ensureStaticColors([]), // Initialize with static colors
        portalTheme: {
            headerText: '', fields: [
                // Include default fields as a good starting point for a new config
                {
                    "name": "Email",
                    "type": "email",
                    "placeholder": "Enter your email",
                    "options": [],

                },
                {
                    "name": "Gender",
                    "type": "select",
                    "options": [
                        "Male",
                        "Female",
                        "Other"
                    ],

                },
                {
                    "name": "Today I would say…",
                    "type": "select",
                    "options": [
                        "Saved",
                        "Rededicated",
                        "Want to go deeper",
                        "Already following",
                        "Not ready yet"
                    ],

                },
                {
                    "name": "Would you like to learn about campus ministries near you?",
                    "type": "select",
                    "options": [
                        "Yes",
                        "No",
                        "Already connected"
                    ],

                }
            ],
        },
        completionPage: {
            "dynamicButtons": dynamicButtons,
        }
    };

    // Initialize config, ensuring static colors are present
    const initialConfigWithDefaults = {
        ...(initialConfig || BLANK_CONFIG),
        style: ensureStaticColors(initialConfig?.style || [])
    };

    const [config, setConfig] = useState(initialConfigWithDefaults);
    const navigate = useNavigate();

    // Use effect to reset form if initialConfig changes (e.g., switching from Create to Update)
    useEffect(() => {
        const newConfig = {
            ...(initialConfig || BLANK_CONFIG),
            style: ensureStaticColors(initialConfig?.style || [])
        };
        setConfig(newConfig);
    }, [initialConfig]);

    // --- State Management Handlers ---

    const handleGeneralChange = (e) => {
        const { name, value } = e.target;
        setConfig(prev => ({ ...prev, [name]: value }));
    };

    const handlePortalHeaderChange = (e) => {
        setConfig(prev => ({
            ...prev,
            portalTheme: { ...prev.portalTheme, headerText: e.target.value }
        }));
    };

    const handleStyleChange = (index, key, value) => {
        const newStyle = [...config.style];
        newStyle[index] = { [key]: value };
        setConfig(prev => ({ ...prev, style: newStyle }));
    };

    // --- Field Handlers ---

    // Handles changes to basic field properties (name, type, placeholder)
    const handleFieldChange = (index, e) => {
        const { name, value } = e.target;
        const newFields = [...config.portalTheme.fields];
        newFields[index] = { ...newFields[index], [name]: value };

        // When switching to/from 'select', ensure 'options' are handled correctly
        if (name === 'type') {
            if (value !== 'select') {
                delete newFields[index].options; // Remove options if not a select
            } else if (!newFields[index].options) {
                newFields[index].options = ["Option 1"]; // Add default options if becoming a select
            }
        }

        setConfig(prev => ({
            ...prev,
            portalTheme: { ...prev.portalTheme, fields: newFields }
        }));
    };

    // NEW HANDLER: Updates the options array directly from the OptionListEditor
    const handleOptionsUpdate = (index, newOptions) => {
        const newFields = [...config.portalTheme.fields];
        newFields[index] = { ...newFields[index], options: newOptions };
        setConfig(prev => ({
            ...prev,
            portalTheme: { ...prev.portalTheme, fields: newFields }
        }));
    };

    const addField = () => {
        setConfig(prev => ({
            ...prev,
            portalTheme: {
                ...prev.portalTheme,
                fields: [...prev.portalTheme.fields, { name: `New Field ${prev.portalTheme.fields.length + 1}`, type: 'text', placeholder: 'Enter value', options: [] }]
            }
        }));
    };

    const removeField = (index) => {
        setConfig(prev => ({
            ...prev,
            portalTheme: {
                ...prev.portalTheme,
                fields: prev.portalTheme.fields.filter((_, i) => i !== index)
            }
        }));
    };

    // --- Button Handlers (Unchanged, but kept for context) ---

    const handleButtonChange = (index, e) => {
        const { name, value } = e.target;
        const newButtons = [...config.completionPage.dynamicButtons];
        newButtons[index] = { ...newButtons[index], [name]: value };
        setConfig(prev => ({
            ...prev,
            completionPage: { ...prev.completionPage, dynamicButtons: newButtons }
        }));
    };

    const addButton = () => {
        setConfig(prev => ({
            ...prev,
            completionPage: {
                ...prev.completionPage,
                dynamicButtons: [...prev.completionPage.dynamicButtons, { label: 'New Button', link: 'https://newlink.com' }]
            }
        }));
    };

    const removeButton = (index) => {
        setConfig(prev => ({
            ...prev,
            completionPage: {
                ...prev.completionPage,
                dynamicButtons: prev.completionPage.dynamicButtons.filter((_, i) => i !== index)
            }
        }));
    };

    // --- Submission Handler ---
    const handleSubmit = (e) => {
        e.preventDefault();
        onSave(config);
    };

    return (
        <div className=" min-h-screen w-full text-[var(--primary-text-color)] p-4 sm:p-1">
            <div className="max-w-full mx-auto">
                <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8">
                    <div className='flex items-start space-x-3 justify-center'>
                        <h2 className="text-2xl font-semibold text-[var(--primary-text-color)]  flex gap-3 cursor-pointer"><span className='mt-1 ' onClick={() => onCancel()}><FaArrowLeft /></span></h2>
                        <div>
                            <h2 className="text-2xl font-bold text-[var(--primary-text-color)]">{title}</h2>
                            <p className="text-[var(--primary-text-color)] text-sm mt-1">
                                {isUpdate ? 'Modify existing configuration.' : 'Define a new school’s theme and portal data.'}
                            </p>
                        </div>
                    </div>
                    {/* The Save Button is now bright orange */}
                    {/* <IconButton
                        icon={SaveIcon}
                        label={isSaving ? "Saving..." : "Save Configuration"}
                        onClick={handleSubmit}
                        className={`mt-4 sm:mt-0 bg-orange-500 hover:bg-orange-600 text-white font-semibold disabled:bg-gray-600 shadow-lg shadow-orange-500/30`}
                        disabled={isSaving}
                    /> */}
                </header>

                <form onSubmit={handleSubmit} className="space-y-10">

                    {/* General Information */}
                    <div className={CARD_CLASSES}>
                        <h3 className={SECTION_TITLE_CLASSES} >General Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <label className="block">
                                <span className="text-[var(--primary-text-color)] mb-2 block font-medium">University/Org Name</span>
                                <input type="text" name="name" value={config.name} onChange={handleGeneralChange} className={INPUT_CLASSES} />
                            </label>
                            <label className="block">
                                <span className="text-[var(--primary-text-color)] mb-2 block font-medium">External ID (e.g., 'gcu', 'asu')</span>
                                <input type="text" name="id" value={config.id} onChange={handleGeneralChange} className={`${INPUT_CLASSES} ${isUpdate ? 'bg-[var(--primary-bg-color)] ' : ''}`} readOnly={isUpdate} />
                                {isUpdate && <p className='text-xs text-gray-500 mt-1'>External ID is read-only for updates.</p>}
                            </label>
                            <label className="block md:col-span-2">
                                <span className="text-[var(--primary-text-color)] mb-2 block font-medium">Logo URL</span>
                                <input type="text" name="logo" value={config.logo} onChange={handleGeneralChange} className={INPUT_CLASSES} />
                            </label>
                            {config.logo && (
                                <div className="md:col-span-2 flex items-center space-x-4 p-3 bg-[var(--primary-bg-color)] rounded-xl border border-gray-700">
                                    <span className="text-sm text-[var(--primary-text-color)] font-medium">Logo Preview:</span>
                                    {/* Using a key in the image to force a re-render on URL change, which is a good practice here */}
                                    <img key={config.logo} src={config.logo} alt="Logo Preview" className="h-10 object-contain bg-gray-500 p-2" />
                                </div>
                            )}
                            <label className="block md:col-span-2">
                                <span className="text-[var(--primary-text-color)]mb-2 block font-medium">Portal Header Text</span>
                                <input type="text" name="headerText" value={config.portalTheme.headerText} onChange={handlePortalHeaderChange} className={INPUT_CLASSES} />
                            </label>
                        </div>
                    </div>

                    {/* Theme Colors */}
                    <div className={CARD_CLASSES}>
                        <h3 className={SECTION_TITLE_CLASSES}>Theme Colors</h3>
                        {/* <p className="text-sm text-gray-400 mb-6">These four colors are required. You can customize the label and hex value for each.</p> */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {config.style.map((item, index) => {
                                const [key, value] = Object.entries(item)[0];
                                return (
                                    <div key={key} className="flex flex-col space-y-3 bg-[var(--color-surface-card)] p-4 rounded-xl border border-gray-700 hover:border-gray-600 transition-colors">

                                        <label className='block'>
                                            <span className="text-[var(--primary-text-color)] text-xs block uppercase tracking-wider">Color Label</span>
                                            {/* Label is Read-Only since we are only keeping the four static keys */}
                                            <p className="text-[var(--primary-text-color)] font-medium pb-1 border-b border-gray-600">{key}</p>
                                        </label>

                                        <div className='flex items-center space-x-3'>
                                            {/* Color Picker Input */}
                                            <label title="Color Picker" className="flex-shrink-0">
                                                <input
                                                    type="color"
                                                    value={value}
                                                    onChange={(e) => handleStyleChange(index, key, e.target.value)}
                                                    className="h-10 w-10 cursor-pointer rounded-lg p-0 border-none overflow-hidden"
                                                    style={{ border: `3px solid ${value}` }}
                                                />
                                            </label>

                                            {/* Value - Hex Input (Editable) */}
                                            <label className="flex-grow">
                                                <span className="text-[var(--primary-text-color)] text-xs block uppercase tracking-wider">Hex Value </span>
                                                <input
                                                    type="text"
                                                    value={value}
                                                    onChange={(e) => handleStyleChange(index, key, e.target.value)}
                                                    className={`text-sm w-full p-2 rounded-lg  border border-gray-700 text-[var(--primary-text-color)] focus:ring-1 focus:ring-orange-500`}
                                                    placeholder="#RRGGBB"
                                                />
                                            </label>

                                            {/* Trash Icon (Disabled) - Acknowledging that it cannot be removed */}
                                            {/* <TrashIcon className="w-5 h-5 text-gray-600 cursor-not-allowed flex-shrink-0 ml-3" title="Cannot remove static colors" /> */}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Portal Fields */}
                    <div className={CARD_CLASSES}>
                        <h3 className={SECTION_TITLE_CLASSES}>Portal Input Fields</h3>
                        <div className="space-y-6">
                            {config.portalTheme.fields.map((field, index) => (
                                <div key={index} className="bg-[var(--color-surface-card)] p-4 rounded-xl border border-gray-700 space-y-3 relative">
                                    <div className='flex items-center justify-between border-b border-gray-700/50 pb-2 mb-2'>
                                        <span className='text-sm font-bold text-orange-400'>Field {index + 1}</span>
                                        {/* <IconButton
                                            icon={TrashIcon} label="Remove Field"
                                            onClick={() => removeField(index)}
                                            className="text-red-400 hover:bg-red-900/50 p-1"
                                        /> */}
                                    </div>
                                    <label className="block">
                                        <span className="text-[var(--primary-text-color)] text-sm block mb-1">Name / Label</span>
                                        <input type="text" name="name" readOnly value={field.name} onChange={(e) => handleFieldChange(index, e)} className={INPUT_CLASSES + " read-only"} />
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <label className="block">
                                            <span className="text-[var(--primary-text-color)] text-sm block mb-1">Type</span>
                                            <select name="type" value={field.type} onChange={(e) => handleFieldChange(index, e)} className={INPUT_CLASSES}>
                                                {/* <option value="text">Text</option>
                                                <option value="email">Email</option>
                                                <option value="select">Select/Dropdown</option> */}
                                                <option value={field.type} defaultChecked>{field.type}</option>
                                            </select>
                                        </label>
                                        {(field.type === 'text' || field.type === 'email') && (
                                            <label className="block">
                                                <span className="text-[var(--primary-text-color)] text-sm block mb-1">Placeholder</span>
                                                <input type="text" name="placeholder" value={field.placeholder || ''} onChange={(e) => handleFieldChange(index, e)} className={INPUT_CLASSES} />
                                            </label>
                                        )}
                                    </div>
                                    {/* REPLACED: Old comma-separated textarea with new OptionListEditor */}
                                    {field.type === 'select' && (
                                        <OptionListEditor
                                            options={field.options || []}
                                            onUpdateOptions={(newOptions) => handleOptionsUpdate(index, newOptions)}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                        {/* <div className="mt-6">
                            <IconButton
                                icon={PlusIcon}
                                label="Add New Field"
                                onClick={addField}
                                className={`bg-gray-700 hover:bg-gray-600 text-gray-300`}
                            />
                        </div> */}
                    </div>

                    {/* Completion Page Buttons */}
                    <div className={CARD_CLASSES}>
                        <h3 className={SECTION_TITLE_CLASSES}>Completion Page Buttons</h3>
                        <div className="space-y-6">
                            {config.completionPage.dynamicButtons.map((button, index) => (
                                <div key={index} className="flex items-start space-x-4 bg-[var(--primary-bgolor)] p-4 rounded-xl border border-gray-700">
                                    <div className="flex-grow grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                                        <label className="block">
                                            <span className="text-[var(--primary-text-color)] text-xs block mb-1">Button Label</span>
                                            <input type="text" name="label" readOnly value={button.label} onChange={(e) => handleButtonChange(index, e)} className={INPUT_CLASSES} />
                                        </label>
                                        <label className="block">
                                            <span className="text-[var(--primary-text-color)] text-xs block mb-1">Link URL</span>
                                            <input type="url" name="link" value={button.link} onChange={(e) => handleButtonChange(index, e)} className={INPUT_CLASSES} />
                                        </label>
                                    </div>

                                </div>
                            ))}
                        </div>

                    </div>

                </form>

                {/* Fixed bottom save bar for large forms */}
                <div className="sticky bottom-0 left-0 right-0   p-4 mt-10 shadow-t-lg flex justify-end">
                    <IconButton
                        icon={SaveIcon}
                        label={isSaving ? "Saving..." : "Save Configuration"}
                        onClick={handleSubmit}
                        className={`bg-orange-500 hover:bg-orange-600 text-white font-semibold disabled:bg-gray-600 shadow-lg shadow-orange-500/30`}
                        disabled={isSaving}
                    />
                </div>

            </div>
        </div>
    );
};

export default ThemePortalConfig;